/**
 * ServiceChargePage — the single Service Charge experience in Accounts.
 *
 * Authority chain, shown in this order:
 *   Collected from Sales → Allocated in People → Settled by Accounts
 *
 * Every business value is produced by GET /service-charge/allocation-state. This page
 * formats; it never derives an employee's share, a pool or an outstanding amount.
 */
import React, { useState, useCallback, useEffect } from 'react';
import { accountsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  AlertTriangle, ArrowRight, BadgeCheck, Download, RefreshCw, Undo2, Users, Wallet,
} from 'lucide-react';

const fmtINR = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (Number.isNaN(n)) return '—';
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const TONE = {
  ok: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  info: 'bg-sky-500/10 text-sky-300 border-sky-500/30',
  warn: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
  bad: 'bg-rose-500/10 text-rose-300 border-rose-500/30',
  mute: 'bg-zinc-800/60 text-zinc-400 border-zinc-700',
};

const Chip = ({ label, value, tone = TONE.mute, testId }) => (
  <div className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] ${tone}`} data-testid={testId}>
    <span className="opacity-60">{label}</span>
    <span className="font-medium">{value}</span>
  </div>
);

const Figure = ({ label, value, accent = 'text-zinc-100', testId, sub }) => (
  <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 px-4 py-3" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
    <div className={`mt-1 text-xl sm:text-2xl font-semibold tabular-nums ${accent}`}>{value}</div>
    {sub && <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div>}
  </div>
);

const MESSAGES = {
  SERVICE_CHARGE_POOL_MISMATCH: 'Service charge cannot be paid until what Sales collected and what People allocated agree.',
  SERVICE_CHARGE_RETENTION_MISMATCH: "People's distributable and retained amounts do not add up to the total collected.",
  SERVICE_CHARGE_ALLOCATION_SUM_MISMATCH: 'The employee allocations do not add up to the distributable amount.',
  SERVICE_CHARGE_NOT_LOCKED: "People has not locked this period's service charge yet.",
  PEOPLE_UNAVAILABLE: 'People could not be reached, so the allocation was not read.',
  PEOPLE_SERVICE_CHARGE_TENANT_MISMATCH: 'That allocation belongs to another outlet. Nothing was stored.',
  SERVICE_CHARGE_ALLOCATION_NOT_SYNCED: "Sync People's allocation before recording a payout.",
  SERVICE_CHARGE_PAYOUT_EXCEEDS_OUTSTANDING: 'That is more than the outstanding amount for this person.',
  OVERPAID_REVIEW_REQUIRED: 'This person has been paid more than their current allocation. Someone needs to review it first.',
  SERVICE_CHARGE_ALLOCATION_MOVED_TO_PEOPLE: 'Service charge is allocated in People. Accounts records and settles it.',
  OUTLET_SCOPE_DENIED: "You can only work with your own outlet's service charge.",
  SERVICE_CHARGE_PAYOUT_NOT_REVERSIBLE: 'Only a completed payout can be reversed.',
  SERVICE_CHARGE_REVERSAL_REASON_REQUIRED: 'Say why this payout is being reversed.',
  SERVICE_CHARGE_PAYOUT_TENANT_MISMATCH: 'That payout belongs to another outlet. Nothing was changed.',
  SERVICE_CHARGE_PAYOUT_NOT_FOUND: 'That payout no longer exists.',
  STALE_REVISION_REJECTED: 'That allocation is older than the one Accounts already holds, so it was ignored.',
};

const thisMonth = () => new Date().toISOString().slice(0, 7);

const ServiceChargePage = () => {
  const [period, setPeriod] = useState('2026-09');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [reverseFor, setReverseFor] = useState(null);
  const [reverseReason, setReverseReason] = useState('');

  const load = useCallback(async (p) => {
    setLoading(true); setError(null);
    try {
      const res = await accountsAPI.getServiceChargeAllocationState(p || period);
      setData(res.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(MESSAGES[d?.code] || d?.message || d || e.message);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(period); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sync = async () => {
    setBusy(true); setNotice(null); setError(null);
    try {
      const res = await accountsAPI.syncServiceChargeAllocation(period);
      const o = res.data;
      setNotice(o.outcome === 'REPLAY_NOOP'
        ? 'Allocation already up to date — nothing changed.'
        : `People's locked allocation stored (revision ${o.source_revision}). No payment was made.`);
      await load(period);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(MESSAGES[d?.code] || d?.message || d || e.message);
    } finally { setBusy(false); }
  };

  const recordPayout = async (employeeId) => {
    setBusy(true); setNotice(null); setError(null);
    try {
      const res = await accountsAPI.createServiceChargePayout({
        period, employee_id: employeeId, amount: parseFloat(payAmount),
        payout_date: new Date().toISOString().slice(0, 10), payment_method: 'BANK',
      });
      setNotice(res.data.outcome === 'REPLAY_NOOP'
        ? 'This payout was already recorded — nothing changed.'
        : `Payout of ${fmtINR(res.data.amount)} recorded. Outstanding now ${fmtINR(res.data.outstanding)}.`);
      setPayFor(null); setPayAmount('');
      await load(period);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(MESSAGES[d?.code] || d?.message || d || e.message);
    } finally { setBusy(false); }
  };

  const reversePayout = async (payout) => {
    setBusy(true); setNotice(null); setError(null);
    try {
      const res = await accountsAPI.reverseServiceChargePayout(payout.payout_id, {
        reason: reverseReason,
      });
      setNotice(res.data.outcome === 'REPLAY_NOOP'
        ? 'This payout was already reversed — nothing changed.'
        : `Payout of ${fmtINR(res.data.amount)} reversed. Outstanding is back to ${fmtINR(res.data.outstanding)}. People's allocation is unchanged.`);
      setReverseFor(null); setReverseReason('');
      await load(period);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(MESSAGES[d?.code] || d?.message || d || e.message);
    } finally { setBusy(false); }
  };

  const alloc = data?.allocation || {};
  const settle = data?.settlement || {};
  const recon = data?.reconciliation || {};
  const legacy = data?.legacy_allocations || {};

  return (
    <div className="p-4 sm:p-6 space-y-5 max-w-5xl mx-auto" data-testid="service-charge-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold text-zinc-100">Service Charge</h1>
        <p className="text-[12px] text-zinc-500 mt-1 flex flex-wrap items-center gap-1">
          <span>Collected from Sales</span><ArrowRight className="w-3 h-3" />
          <span>Allocated in People</span><ArrowRight className="w-3 h-3" />
          <span>Settled by Accounts</span>
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          value={period}
          onChange={(e) => setPeriod(e.target.value.trim())}
          placeholder={`Period, e.g. ${thisMonth()}`}
          className="bg-zinc-900 border-zinc-800 h-11"
          data-testid="sc-period-input"
        />
        <Button onClick={() => load(period)} disabled={loading || !period}
                className="h-11 bg-zinc-100 text-zinc-900 hover:bg-white" data-testid="sc-load-button">
          {loading ? 'Loading…' : 'Open period'}
        </Button>
      </div>

      {error && (
        <Card className="border-rose-500/30 bg-rose-950/20" data-testid="sc-error">
          <CardContent className="p-4 text-[12px] text-rose-200 flex gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
          </CardContent>
        </Card>
      )}
      {notice && (
        <Card className="border-zinc-700 bg-zinc-900/60" data-testid="sc-notice">
          <CardContent className="p-3 text-[12px] text-zinc-300">{notice}</CardContent>
        </Card>
      )}

      {data && (
        <>
          <Card className="border-zinc-800 bg-zinc-900/30" data-testid="sc-summary-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-zinc-100 flex items-center gap-2">
                <Wallet className="w-4 h-4 text-zinc-400" /> {data.period}
                {(data.outlet_name || data.outlet_id) && (
                  <span className="text-xs font-normal text-zinc-400 truncate" data-testid="sc-outlet">
                    · {data.outlet_name || data.outlet_id}
                  </span>
                )}
              </CardTitle>
              <div className="flex flex-wrap gap-2 pt-2">
                <Chip label="Allocation" value={alloc.synced ? (alloc.status === 'LOCKED' ? 'Locked' : alloc.status) : 'Not synced'}
                      tone={alloc.synced ? TONE.info : TONE.warn} testId="sc-chip-allocation" />
                {alloc.revision != null && (
                  <Chip label="Revision" value={alloc.revision} tone={TONE.mute} testId="sc-chip-revision" />
                )}
                <Chip label="Reconciliation"
                      value={recon.state === 'RECONCILED' ? 'Reconciled' : recon.state === 'NOT_SYNCED' ? 'Not synced' : 'Needs reconciliation'}
                      tone={recon.state === 'RECONCILED' ? TONE.ok : TONE.bad} testId="sc-chip-reconciliation" />
                <Chip label="Settlement"
                      value={settle.state === 'SETTLED' ? 'Settled' : settle.state === 'PARTIALLY_SETTLED' ? 'Part paid'
                        : settle.state === 'OVERPAID_REVIEW_REQUIRED' ? 'Needs review' : 'Outstanding'}
                      tone={settle.state === 'SETTLED' ? TONE.ok : settle.state === 'OVERPAID_REVIEW_REQUIRED' ? TONE.bad : TONE.warn}
                      testId="sc-chip-settlement" />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Figure label="Collected from Sales" value={fmtINR(data.collected?.amount)}
                        sub={`${data.collected?.txn_count ?? 0} bills`} testId="sc-figure-collected" />
                <Figure label="Allocated by People" value={fmtINR(alloc.distributable)}
                        sub={alloc.employee_count != null ? `${alloc.employee_count} employee${alloc.employee_count === 1 ? '' : 's'}` : null}
                        testId="sc-figure-allocated" />
                <Figure label="Retained" value={fmtINR(alloc.retained)} testId="sc-figure-retained" />
                <Figure label="Paid by Accounts" value={fmtINR(settle.paid)} testId="sc-figure-paid" />
              </div>
              <Figure label="Outstanding to the team" value={fmtINR(settle.outstanding)}
                      accent="text-emerald-300" testId="sc-figure-outstanding" />

              {recon.state === 'NEEDS_RECONCILIATION' && (
                <div className="rounded-lg border border-rose-500/30 bg-rose-950/20 p-3 text-[12px] text-rose-200"
                     data-testid="sc-reconciliation-warning">
                  Service charge cannot be paid until what Sales collected
                  ({fmtINR(recon.sales_collected)}) and what People allocated
                  ({fmtINR(recon.people_total_collected)}) agree.
                </div>
              )}
              {settle.overpaid_review_required && (
                <div className="rounded-lg border border-rose-500/30 bg-rose-950/20 p-3 text-[12px] text-rose-200"
                     data-testid="sc-overpaid-warning">
                  Someone has been paid more than their current allocation. A person needs to
                  review this before any further payout — Accounts will not claw anything back
                  on its own.
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-2 pt-1">
                <Button onClick={sync} disabled={busy} data-testid="sc-sync-button"
                        className="h-11 bg-sky-600 hover:bg-sky-500">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {busy ? 'Working…' : 'Sync allocation from People'}
                </Button>
              </div>
              <p className="text-[11px] text-zinc-500" data-testid="sc-authority-note">
                Employee shares are decided in People. Accounts records what Sales collected,
                stores People's locked allocation and settles it.
              </p>
            </CardContent>
          </Card>

          <Card className="border-zinc-800 bg-zinc-900/30" data-testid="sc-employees-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-zinc-100 flex items-center gap-2">
                <Users className="w-4 h-4 text-zinc-400" /> Allocated in People
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {(data.employees || []).length === 0 && (
                <p className="text-[12px] text-zinc-500" data-testid="sc-no-employees">
                  No allocation synced for this period yet.
                </p>
              )}
              {(data.employees || []).map((e) => (
                <div key={e.employee_id} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-3 space-y-2"
                     data-testid={`sc-employee-${e.employee_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-zinc-100 text-sm font-medium">{e.employee_name || e.employee_id}</div>
                      <div className="text-[11px] text-zinc-500">{e.employee_id}</div>
                    </div>
                    <Chip label="" value={e.settlement_status === 'OVERPAID_REVIEW_REQUIRED' ? 'Needs review'
                      : e.settlement_status === 'SETTLED' ? 'Settled'
                        : e.settlement_status === 'PARTIALLY_SETTLED' ? 'Part paid' : 'Outstanding'}
                          tone={e.settlement_status === 'SETTLED' ? TONE.ok
                            : e.settlement_status === 'OVERPAID_REVIEW_REQUIRED' ? TONE.bad : TONE.warn}
                          testId={`sc-employee-status-${e.employee_id}`} />
                  </div>
                  <div className="space-y-2 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-2 text-[12px]">
                    <div className="flex items-baseline justify-between sm:block border-b border-zinc-800/60 pb-1.5 sm:border-0 sm:pb-0">
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500">Allocated</div>
                      <div className="text-zinc-100 tabular-nums sm:mt-1" data-testid={`sc-allocated-${e.employee_id}`}>{fmtINR(e.allocated)}</div>
                    </div>
                    <div className="flex items-baseline justify-between sm:block border-b border-zinc-800/60 pb-1.5 sm:border-0 sm:pb-0">
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500">Paid</div>
                      <div className="text-zinc-100 tabular-nums sm:mt-1" data-testid={`sc-paid-${e.employee_id}`}>{fmtINR(e.paid)}</div>
                    </div>
                    <div className="flex items-baseline justify-between sm:block">
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500">Outstanding</div>
                      <div className="text-emerald-300 tabular-nums sm:mt-1" data-testid={`sc-outstanding-${e.employee_id}`}>{fmtINR(e.outstanding)}</div>
                    </div>
                  </div>
                  {recon.payout_allowed && parseFloat(e.outstanding || 0) > 0 && (
                    payFor === e.employee_id ? (
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Input value={payAmount} onChange={(ev) => setPayAmount(ev.target.value)}
                               placeholder={`Up to ${e.outstanding}`} inputMode="decimal"
                               className="bg-zinc-900 border-zinc-800 h-11"
                               data-testid={`sc-payout-amount-${e.employee_id}`} />
                        <Button onClick={() => recordPayout(e.employee_id)} disabled={busy || !payAmount}
                                className="h-11 bg-emerald-600 hover:bg-emerald-500"
                                data-testid={`sc-payout-confirm-${e.employee_id}`}>Confirm payout</Button>
                        <Button variant="ghost" className="h-11" onClick={() => { setPayFor(null); setPayAmount(''); }}
                                data-testid={`sc-payout-cancel-${e.employee_id}`}>Cancel</Button>
                      </div>
                    ) : (
                      <Button variant="outline" className="h-11 w-full sm:w-auto border-zinc-700"
                              onClick={() => { setPayFor(e.employee_id); setPayAmount(''); }}
                              data-testid={`sc-payout-button-${e.employee_id}`}>
                        <Download className="w-4 h-4 mr-2" /> Record payout
                      </Button>
                    )
                  )}
                </div>
              ))}
              {alloc.source_identity && (
                <p className="text-[10px] font-mono text-zinc-600 break-all pt-1" data-testid="sc-source-identity">
                  {alloc.source_identity}
                </p>
              )}
            </CardContent>
          </Card>

          {(data.settlement_history || []).length > 0 && (
            <Card className="border-zinc-800 bg-zinc-900/30" data-testid="sc-history-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base text-zinc-100 flex items-center gap-2">
                  <Undo2 className="w-4 h-4 text-zinc-400" /> Settlement history
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.settlement_history.map((p) => (
                  <div key={p.payout_id}
                       className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-3 space-y-2"
                       data-testid={`sc-history-${p.payout_id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm text-zinc-100 truncate">
                          {p.recipient_name || p.employee_id}
                        </div>
                        <div className="text-[11px] text-zinc-500">
                          Paid {p.payout_date} · {p.payment_method} · revision {p.people_source_revision}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className={`text-sm tabular-nums ${p.counts_as_paid ? 'text-zinc-100' : 'text-zinc-500 line-through'}`}
                             data-testid={`sc-history-amount-${p.payout_id}`}>
                          {fmtINR(p.amount)}
                        </div>
                        <Chip label="" value={p.status === 'REVERSED' ? 'Reversed' : 'Paid'}
                              tone={p.status === 'REVERSED' ? TONE.bad : TONE.ok}
                              testId={`sc-history-status-${p.payout_id}`} />
                      </div>
                    </div>

                    {p.reversal && (
                      <div className="rounded-lg border border-rose-500/20 bg-rose-950/10 p-2.5 text-[12px] text-rose-200"
                           data-testid={`sc-reversal-${p.payout_id}`}>
                        <div className="flex items-center justify-between gap-3">
                          <span>Reversed on {p.reversal.reversal_date}</span>
                          <span className="tabular-nums" data-testid={`sc-reversal-amount-${p.payout_id}`}>
                            −{fmtINR(p.reversal.amount)}
                          </span>
                        </div>
                        <div className="text-rose-200/70 mt-1">{p.reversal.reason}</div>
                      </div>
                    )}

                    {p.reversible && (
                      reverseFor === p.payout_id ? (
                        <div className="rounded-lg border border-zinc-700 bg-zinc-900/60 p-3 space-y-2"
                             data-testid={`sc-reverse-confirm-panel-${p.payout_id}`}>
                          <p className="text-[12px] text-zinc-300">
                            Reverse {fmtINR(p.amount)} paid to{' '}
                            <span className="text-zinc-100">{p.recipient_name || p.employee_id}</span>{' '}
                            on {p.payout_date} by {p.payment_method}?
                          </p>
                          <p className="text-[11px] text-zinc-500">
                            This does not change the People allocation. It reverses the recorded
                            Accounts settlement, puts the amount back as outstanding to the team
                            and credits it back in the P&amp;L. The original payout stays in history.
                          </p>
                          <Input value={reverseReason} onChange={(ev) => setReverseReason(ev.target.value)}
                                 placeholder="Why is this being reversed?"
                                 className="bg-zinc-900 border-zinc-800 h-11"
                                 data-testid={`sc-reverse-reason-${p.payout_id}`} />
                          <div className="flex flex-col sm:flex-row gap-2">
                            <Button onClick={() => reversePayout(p)}
                                    disabled={busy || reverseReason.trim().length < 4}
                                    className="h-11 bg-rose-600 hover:bg-rose-500"
                                    data-testid={`sc-reverse-confirm-${p.payout_id}`}>
                              {busy ? 'Working…' : 'Reverse payout'}
                            </Button>
                            <Button variant="ghost" className="h-11"
                                    onClick={() => { setReverseFor(null); setReverseReason(''); }}
                                    data-testid={`sc-reverse-cancel-${p.payout_id}`}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <Button variant="outline" className="h-11 w-full sm:w-auto border-zinc-700"
                                onClick={() => { setReverseFor(p.payout_id); setReverseReason(''); }}
                                data-testid={`sc-reverse-button-${p.payout_id}`}>
                          <Undo2 className="w-4 h-4 mr-2" /> Reverse payout
                        </Button>
                      )
                    )}
                  </div>
                ))}
                <p className="text-[11px] text-zinc-500" data-testid="sc-history-note">
                  Payouts are never deleted. A reversal is recorded alongside the original.
                </p>
              </CardContent>
            </Card>
          )}

          {(data.revisions || []).length > 0 && (
            <Card className="border-zinc-800 bg-zinc-900/20" data-testid="sc-revisions-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-zinc-300 flex items-center gap-2">
                  <BadgeCheck className="w-4 h-4 text-zinc-500" /> Allocation history
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.revisions.map((r) => (
                  <div key={r.source_revision} className="flex flex-wrap items-center justify-between gap-2 text-[12px]"
                       data-testid={`sc-revision-${r.source_revision}`}>
                    <span className="text-zinc-200">
                      Revision {r.source_revision}
                      <span className="text-zinc-500">
                        {r.superseded_by_revision ? ` · superseded by ${r.superseded_by_revision}` : ' · current'}
                      </span>
                    </span>
                    <span className="text-zinc-400 tabular-nums">{fmtINR(r.distributable_amount)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {legacy.count > 0 && (
            <Card className="border-zinc-800 bg-zinc-900/20" data-testid="sc-legacy-card">
              <CardContent className="p-4 text-[12px] text-zinc-400">
                <span className="text-zinc-300">Legacy history — no longer allocation authority.</span>
                {' '}{legacy.count} allocation{legacy.count === 1 ? '' : 's'} created in Accounts before People
                owned allocation. Kept for audit; never used for payouts or outstanding.
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default ServiceChargePage;
